import React, { useMemo, useState } from 'react';
import { Column, Row, Section, SectionHeader, Text } from '@ydistri/ds';
import {
  SkuRedistributionChangelog as SkuRedistributionChangelogType,
  SkuRedistributionChangeType,
} from '@ydistri/api-sdk';
import { formatDateTime } from '@ydistri/generic-utils';
import ChangelogSummary from '../ChangelogSummary';
import ChangelogTargetStores from '../../EditRedistributionTargetStore/ChangelogTargetStores';
import ChangelogSourceStores from '../../EditRedistributionSourceStore/ChangelogSourceStores';
import ChangelogRoutes from '../../EditRedistributionRoute/ChangelogRoutes';
import ChangelogPickingPositions from '../../EditRedistributionPickingPositions/ChangelogPickingPositions';
import ChangelogPickingPosition from '../../EditRedistributionPickingPosition/ChangelogPickingPosition';
import { StatisticItem } from '../../StatisticsSummary';

export interface ChangelogSectionProps {
  item: SkuRedistributionChangelogType;
}

const ChangelogSection: React.FC<ChangelogSectionProps> = ({ item }) => {
  const [stats, setStats] = useState<StatisticItem[]>([]);

  const header = useMemo(() => {
    return (
      <Column $gap="1rem">
        <SectionHeader showBulletPoint={true}>
          {item.description ??
            `${item.skuRedistributionChangeType} — ${formatDateTime(item.createdAt)}`}
        </SectionHeader>
        <Row $gap="0.5rem">
          <Text $bold>Date:</Text>
          <Text as="span">{formatDateTime(item.createdAt)}</Text>
        </Row>
      </Column>
    );
  }, [item.description, item.skuRedistributionChangeType, item.createdAt]);

  const content = useMemo(() => {
    if (!item.payload) return null;
    switch (item.skuRedistributionChangeType) {
      case SkuRedistributionChangeType.TargetStoresRemoved:
        return <ChangelogTargetStores stringifiedData={item.payload} onStatsComputed={setStats} />;
      case SkuRedistributionChangeType.SourceStoresRemoved:
        return <ChangelogSourceStores stringifiedData={item.payload} onStatsComputed={setStats} />;
      case SkuRedistributionChangeType.RoutesRemoved:
        return <ChangelogRoutes stringifiedData={item.payload} onStatsComputed={setStats} />;
      case SkuRedistributionChangeType.PickingPositionsRemoved:
        return (
          <ChangelogPickingPositions stringifiedData={item.payload} onStatsComputed={setStats} />
        );
      case SkuRedistributionChangeType.PickingPositionUpdated:
        return (
          <ChangelogPickingPosition stringifiedData={item.payload} onStatsComputed={setStats} />
        );
      case SkuRedistributionChangeType.ProductRemoved: {
        throw new Error('Not implemented yet: SkuRedistributionChangeType.ProductRemoved case');
      }
      default:
        return null;
    }
  }, [item]);

  return (
    <Section
      collapsible={true}
      $contentMarginTop={false}
      header={header}
      $ratio={1}
      headerActions={<ChangelogSummary title="Summary" stats={stats} />}
    >
      {content}
    </Section>
  );
};

export default ChangelogSection;
